import React from 'react';
import { Card, Statistic, Row, Col, Typography, Progress, Space } from 'antd';
import { 
  TrophyOutlined, 
  BarChartOutlined, 
  CheckCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined 
} from '@ant-design/icons';

const { Text, Title } = Typography;

export interface StatItem {
  title: string;
  value: string | number;
  suffix?: string;
  prefix?: React.ReactNode;
  precision?: number;
  valueStyle?: React.CSSProperties;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

export interface ProgressItem {
  title: string;
  percent: number;
  status?: 'success' | 'exception' | 'normal' | 'active';
  strokeColor?: string;
  showInfo?: boolean;
}

export interface StatsCardProps {
  title: string;
  icon?: React.ReactNode;
  stats?: StatItem[];
  progress?: ProgressItem[];
  extra?: React.ReactNode;
  loading?: boolean;
  bordered?: boolean;
  size?: 'default' | 'small';
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  icon = <BarChartOutlined />,
  stats = [],
  progress = [],
  extra,
  loading = false,
  bordered = true,
  size = 'default',
}) => {
  return (
    <Card
      title={
        <Space>
          {icon}
          <Title level={size === 'small' ? 5 : 4} style={{ margin: 0 }}>
            {title}
          </Title>
        </Space>
      }
      extra={extra}
      loading={loading}
      bordered={bordered}
      size={size}
    >
      {stats.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: progress.length > 0 ? 24 : 0 }}>
          {stats.map((stat, index) => (
            <Col xs={24} sm={12} md={stats.length > 2 ? 8 : 12} key={index}>
              <Statistic
                title={stat.title}
                value={stat.value}
                suffix={stat.suffix}
                prefix={stat.prefix}
                precision={stat.precision}
                valueStyle={stat.valueStyle}
              />
              {stat.trend && (
                <div style={{ marginTop: 4 }}>
                  <Text 
                    type={stat.trend.isPositive ? 'success' : 'danger'}
                    style={{ fontSize: '12px' }}
                  >
                    {stat.trend.isPositive ? '↗' : '↘'} {Math.abs(stat.trend.value)}%
                  </Text>
                </div>
              )}
            </Col>
          ))}
        </Row>
      )}

      {progress.length > 0 && (
        <div>
          {progress.map((item, index) => (
            <div key={index} style={{ marginBottom: index < progress.length - 1 ? 16 : 0 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>{item.title}</Text>
                {item.showInfo !== false && (
                  <Text type="secondary" style={{ float: 'right' }}>
                    {item.percent}%
                  </Text>
                )}
              </div>
              <Progress
                percent={item.percent}
                status={item.status}
                strokeColor={item.strokeColor}
                showInfo={item.showInfo}
                size="small"
              />
            </div>
          ))}
        </div>
      )}

      {stats.length === 0 && progress.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <InfoCircleOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">No data available</Text>
          </div>
        </div>
      )}
    </Card>
  );
};

export default StatsCard; 